import { CoreUIColors } from '../../colors';
import { YammerColors } from '../../colors/yammerPalette';

import type {
  CoreUIExtendedThemeBorderRadiusTokenValues,
  CoreUIExtendedThemeTokenName,
  CoreUIExtendedThemeTokenValues,
  CoreUITokenOverrides,
  CoreuiExtendedThemeTokensNamespace,
} from './coreUITokens.types';
import { createTokens } from './createTokensObject';

const tokenMap: Record<CoreUIExtendedThemeTokenName, boolean> = {
  borderRadiusXXLarge: true,
  colorBlueBackground1: true,
  colorBlueBackground1Hover: true,
  colorBlueBackground3: true,
  colorBlueBackground3Hover: true,
  colorBlueBackground3Pressed: true,
  colorBlueBackground3Selected: true,
  colorBlueBackgroundGradient: true,
  colorBlueBorder1: true,
  colorBlueForeground1: true,
  colorBlueForegroundLink: true,
  colorBrandAccentBackground: true,
  colorBrandAccentBackgroundHover: true,
  colorBrandAccentForeground: true,
  colorMagentaBackground3: true,
  colorMagentaForeground1: true,
  colorPurpleBackground3: true,
  colorTealBackground3: true,
  colorThemeDark: true,
  colorWhiteBackground190: true,
};
const tokenNames: CoreUIExtendedThemeTokenName[] = Object.keys(tokenMap) as CoreUIExtendedThemeTokenName[];

const localNamespace: CoreuiExtendedThemeTokensNamespace = 'extendedThemeToken';
const { tokens: coreuiTokens, cssVariables: coreuiCSSVariables } = createTokens(tokenNames, localNamespace);

export { coreuiTokens, coreuiCSSVariables };

const borderRadiusTokenValues: CoreUIExtendedThemeBorderRadiusTokenValues = {
  [coreuiCSSVariables.borderRadiusXXLarge]: '20px',
};

type GetDefaultTokenValues = () => CoreUIExtendedThemeTokenValues;
const getLightCoreUITokenValues: GetDefaultTokenValues = () => ({
  ...borderRadiusTokenValues,
  [coreuiCSSVariables.colorBlueBackground1]: CoreUIColors.blueTint60,
  [coreuiCSSVariables.colorBlueBackground1Hover]: CoreUIColors.blueTint50,
  [coreuiCSSVariables.colorBlueBackground3]: CoreUIColors.blueShade10,
  [coreuiCSSVariables.colorBlueBackground3Hover]: CoreUIColors.blueShade20,
  [coreuiCSSVariables.colorBlueBackground3Pressed]: CoreUIColors.royalBlueShade20,
  [coreuiCSSVariables.colorBlueBackground3Selected]: CoreUIColors.royalBluePrimary,
  [coreuiCSSVariables.colorBlueBackgroundGradient]: CoreUIColors.lightBlueTint50,
  [coreuiCSSVariables.colorBlueForeground1]: CoreUIColors.blueShade10,
  [coreuiCSSVariables.colorBlueBorder1]: CoreUIColors.blueTint40,
  [coreuiCSSVariables.colorBlueForegroundLink]: CoreUIColors.blueShade10,
  [coreuiCSSVariables.colorMagentaBackground3]: CoreUIColors.magentaPrimary,
  [coreuiCSSVariables.colorMagentaForeground1]: CoreUIColors.magentaShade10,
  [coreuiCSSVariables.colorThemeDark]: CoreUIColors.blueShade10,
  [coreuiCSSVariables.colorPurpleBackground3]: CoreUIColors.purplePrimary,
  [coreuiCSSVariables.colorTealBackground3]: CoreUIColors.tealPrimary,
  [coreuiCSSVariables.colorWhiteBackground190]: CoreUIColors.white,
  [coreuiCSSVariables.colorBrandAccentForeground]: YammerColors.yammerPrimary,
  [coreuiCSSVariables.colorBrandAccentBackground]: CoreUIColors.fui9ColorBrandBackground2WebThemeLight,
  [coreuiCSSVariables.colorBrandAccentBackgroundHover]: CoreUIColors.fui9ColorBrandBackground2HoverWebThemeLight,
});

const getDarkCoreUITokenValues: GetDefaultTokenValues = () => ({
  ...borderRadiusTokenValues,
  [coreuiCSSVariables.colorBlueBackground1]: CoreUIColors.blueShade40,
  [coreuiCSSVariables.colorBlueBackground1Hover]: CoreUIColors.darkBluePrimary,
  [coreuiCSSVariables.colorBlueBackground3]: CoreUIColors.blueShade10,
  [coreuiCSSVariables.colorBlueBackground3Hover]: CoreUIColors.blueShade20,
  [coreuiCSSVariables.colorBlueBackground3Pressed]: CoreUIColors.royalBlueShade20,
  [coreuiCSSVariables.colorBlueBackground3Selected]: CoreUIColors.royalBluePrimary,
  [coreuiCSSVariables.colorBlueBackgroundGradient]: CoreUIColors.lightBlueShade40,
  [coreuiCSSVariables.colorBlueForeground1]: CoreUIColors.blueTint30,
  [coreuiCSSVariables.colorBlueBorder1]: CoreUIColors.blueShade10,
  [coreuiCSSVariables.colorBlueForegroundLink]: CoreUIColors.blueTint30,
  [coreuiCSSVariables.colorMagentaBackground3]: CoreUIColors.magentaPrimary,
  [coreuiCSSVariables.colorMagentaForeground1]: CoreUIColors.magentaTint40,
  [coreuiCSSVariables.colorThemeDark]: CoreUIColors.blueTint30,
  [coreuiCSSVariables.colorPurpleBackground3]: CoreUIColors.purplePrimary,
  [coreuiCSSVariables.colorTealBackground3]: CoreUIColors.tealPrimary,
  [coreuiCSSVariables.colorWhiteBackground190]: CoreUIColors.darkGray190,
  [coreuiCSSVariables.colorBrandAccentForeground]: YammerColors.yammerTint30,
  [coreuiCSSVariables.colorBrandAccentBackground]: CoreUIColors.fui9ColorBrandBackground2WebThemeDark,
  [coreuiCSSVariables.colorBrandAccentBackgroundHover]: CoreUIColors.fui9ColorBrandBackground2HoverWebThemeDark,
});

type GetCoreUITokenValues = (
  isDarkMode: boolean,
  tokenOverrides: CoreUITokenOverrides | undefined
) => CoreUIExtendedThemeTokenValues;
export const getCoreUITokenValues: GetCoreUITokenValues = (isDarkMode, tokenOverrides = {}) => {
  const baseTokenValues = isDarkMode ? getDarkCoreUITokenValues() : getLightCoreUITokenValues();

  const overrides = Object.entries(tokenOverrides).reduce<Partial<CoreUIExtendedThemeTokenValues>>(
    (acc, [tokenName, tokenValue]) => {
      if (tokenValue && tokenName in coreuiCSSVariables) {
        const cssVariable = coreuiCSSVariables[tokenName as CoreUIExtendedThemeTokenName];
        acc[cssVariable] = tokenValue;
      }

      return acc;
    },
    {}
  );

  return {
    ...baseTokenValues,
    ...overrides,
  };
};
